import { CarsharingController } from "./carsharingcontroller.js";
class _Carsharing {
  constructor() {
    /* Ensure we always have a single instance of Carsharing class */
    if (_Carsharing._instance) {
      return _Carsharing._instance;
    }
    _Carsharing._instance = this;
    this.carsharingController = new CarsharingController();
  }

  /**
   * Initializes Carsharing component.
   *
   * @memberof Carsharing
   *
   * @example
   * Carsharing.init()
   * .then(() => { })
   * .catch((error) => { });
   */
  init(callback) {
    return this.carsharingController.init(callback);
  }

  /**
   * Gets the active rental.
   *
   * @returns {Rental} response Response with the rental data.
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getRental()
   * .then((rental) => { })
   * .catch((error) => { });
   */
  getRental(overrideQueryParams) {
    return this.carsharingController.getRental(overrideQueryParams);
  }

  /**
   * @typedef {Object} TripResponse Response with the past trips data.
   * @property {Trip[]} trips List of past trips upto the given pageSize.
   * @property {number} totalCount Total number of past trips that can be retrieved.
   */

  /**
   * Gets the past rental trips.
   * @param {number} pageNumber Start page number.
   * @param {number} pageSize Page size for number of trips to return.
   * @param {boolean} includeCancelled Boolean to indicate if cancelled trips should be included.
   *
   * @returns {TripResponse}
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getPastTrips(1, 10, false)
   * .then((response) => {
   *   console.log(response.trips)
   *   console.log(response.totalCount)
   * })
   * .catch((error) => { });
   *
   */
  getTrips(queryParams) {
    return this.carsharingController.getTrips(queryParams);
  }

  /**
   * Gets the upcoming rental trips.
   * @param {number} pageNumber Start page number.
   * @param {number} pageSize Page size for number of trips to return.
   *
   * @returns {TripResponse}
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getUpcomingTrips(1, 10)
   * .then((response) => {
   *   console.log(response.trips)
   *   console.log(response.totalCount)
   * })
   * .catch((error) => { });
   */
  getUpcomingTrips(pageNumber, pageSize, overrideQueryParams) {
    return this.carsharingController.getUpcomingTrips(
      pageNumber,
      pageSize,
      overrideQueryParams
    );
  }

  /**
   * Gets all markets for tenant.
   *
   * @returns {Market[]} List of markets.
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getMarkets()
   * .then((markets) => { }))
   * .catch((error) => { });
   */
  getMarkets(overrideQueryParams) {
    return this.carsharingController.getMarkets(overrideQueryParams);
  }

  /**
   * Get a market by id..
   *
   * @returns {MarketResponse}
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getMarket({ market_id: 1 })
   * .then((market) => { }))
   * .catch((error) => { });
   */
  getMarket(overrideQueryParams) {
    return this.carsharingController.getMarket(overrideQueryParams);
  }

  /**
   * Gets the stations for given market.
   *
   * @returns List of all stations.
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getStations({ market_id: 1 })
   * .then((stations) => { }))
   * .catch((error) => { });
   */
  getStations(queryParams) {
    return this.carsharingController.getStations(queryParams);
  }

  /**
   * Gets the available start dates for given service.
   *
   * @param {String} service_id for which start dates need to be retrieved.
   *
   * @returns {Strings[]} List of dates.
   * @memberof Carsharing
   */
  getServiceAvailableStartDates(queryParams) {
    return this.carsharingController.getServiceAvailableStartDates(queryParams);
  }

  /**
   * Gets the available start times for given service.
   * @param {String} service_id for which start times need to be retrieved.
   * @param {String} startDate Selected start date to get start times for eg: "2020-12-21T10:10:00-08:00"
   *
   * @returns {Strings[]} List of dates.
   * @memberof Carsharing
   */
  getServiceAvailableStartTimes(queryParams) {
    return this.carsharingController.getServiceAvailableStartTimes(queryParams);
  }

  /**
   * Gets the available start dates for given station.
   *
   * @param {Station} station Station for which start dates need to be retrieved.
   *
   * @returns {Strings[]} List of dates.
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getAvailableStartDates({ station_id: station.id })
   * .then((startDates) => { }))
   * .catch((error) => { });
   */
  getAvailableStartDates(queryParams) {
    return this.carsharingController.getAvailableStartDates(queryParams);
  }

  /**
   * Gets the available start times for given station.
   * @param {Station} station Station for which start times need to be retrieved.
   * @param {String} startDate Selected start date to get start times for eg: "2020-12-21T10:10:00-08:00"
   *
   * @returns {Strings[]} List of dates.
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getAvailableStartTimes(station, startDate)
   * .then((startTimes) => { }))
   * .catch((error) => { });
   */
  getAvailableStartTimes(queryParams) {
    return this.carsharingController.getAvailableStartTimes(queryParams);
  }

  /**
   * Gets the available end dates for given station.
   * @param {Station} station Station for which end dates need to be retrieved.
   * @param {String} startDateTime Selected fully qualified start datetime to get end dates for eg: "2020-12-21T10:10:00-08:00"
   *
   * @returns {Strings[]} List of dates.
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getAvailableEndDates(station, startDateTime)
   * .then((endDates) => { }))
   * .catch((error) => { });
   */
  getAvailableEndDates(queryParams) {
    return this.carsharingController.getAvailableEndDates(queryParams);
  }

  /**
   * Gets the available end dates for given station.
   * @param {Station} station Station for which end times need to be retrieved.
   * @param {String} startDateTime Selected fully qualified start datetime to get end dates for eg: "2020-12-21T10:10:00-08:00"
   * @param {String} endDate Selected end date to get end times for.
   *
   * @returns {Strings[]} List of dates.
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getAvailableEndTimes(station, startDateTime, endDate)
   * .then((endTimes) => { }))
   * .catch((error) => { });
   */
  getAvailableEndTimes(queryParams) {
    return this.carsharingController.getAvailableEndTimes(queryParams);
  }

  /**
   * Gets the available vehicle types for given station.
   * @param {Station} station Station for which available vehicle types need to be retrieved.
   * @param {String} startDateTime Selected fully qualified start datetime to get available vehicle types for eg: "2020-12-21T10:10:00-08:00"
   * @param {String} endDateTime Selected fully qualified end datetime to get available vehicle types for.
   *
   * @returns {AvailableVehicleTypes[]} List of available vehicle types.
   * @memberof Carsharing
   *
   * @example
   * Carsharing.getAvailableVehicleTypes({ station_id: station.id, start_date_time: startDateTime, end_date_time: endDateTime })
   * .then((vehicleTypes) => { }))
   * .catch((error) => { });
   */
  getAvailableVehicleTypes(queryParams) {
    return this.carsharingController.getAvailableVehicleTypes(queryParams);
  }

  /**
   * Schedules a rental.
   *
   * @param {Station} station Station for which available vehicle types need to be retrieved.
   * @param {String} startDateTime Selected fully qualified start datetime to get available vehicle types for eg: "2020-12-21T10:10:00-08:00"
   * @param {String} endDateTime Selected fully qualified end datetime to get available vehicle types for.
   * @param {AvailableVehicleTypes} vehicleType Vehicle type to schedule.
   *
   * @memberof Carsharing
   *
   * @example
   * Carsharing.scheduleRental({ station_id: station.id, start_date_time: startDateTime, end_date_time: endDateTime, vehicle_type_id: vehicletype.id })
   * .then((scheduledRental) => { }))
   * .catch((error) => { });
   */
  scheduleRental(params) {
    return this.carsharingController.scheduleRental(params);
  }

  /**
   * Cancels a scheduled rental.
   *
   * @param {number} scheduledRentalId Identifier for scheduled rental.
   *
   * @memberof Carsharing
   *
   * @example
   * Carsharing.cancelScheduledRental(scheduledRentalId)
   * .then(() => { }))
   * .catch((error) => { });
   */
  cancelScheduledRental(queryParams) {
    return this.carsharingController.cancelScheduledRental(queryParams);
  }

  /**
   *
   * Carsharing.getPolicyDocuments()
   * .then(() => {})
   * .catch(() => {})
   *@memberof Carsharing
   */

  getPolicyDocuments() {
    return this.carsharingController.getPolicyDocuments();
  }

  /**
   *
   * @returns result list with phone Number
   * @memberof Carsharing
   */

  getSupportPhoneNumber() {
    return this.carsharingController.getSupportPhoneNumber();
  }

  /**
   *
   * @param {Number} id
   * @memberof Carsharing
   */

  associateCustomerToBusiness(queryParams) {
    return this.carsharingController.associateCustomerToBusiness(queryParams);
  }

  /**
   * Gets vehicle lists associated to business entity
   * @param {Number} id
   *
   *
   * Carsharing.getVehicleList(1)
   * .then(() => {})
   * .catch(() => {})
   *
   * @memberof Carsharing
   */

  getVehicleList(queryParams) {
    return this.carsharingController.getVehicleList(queryParams);
  }

  /**
   * Gets Rental Info
   *
   * @param {Number} id
   *
   * Carsharing.getRentalInfo(1)
   * .then(() => {})
   * .catch(() => {})
   *
   * @memberof Carsharing
   */
  getRentalInfo(queryParams) {
    return this.carsharingController.getRentalInfo(queryParams);
  }

  /**
   * Associate Vehicle and Driver
   * @param {Number} fleetRentalId
   * @param {Number} customerId
   * @param {Number} fleetRentvehicleIdalId
   *
   * Carsharing.associateVehicleDriver(1, 2, 3)
   * .then(() => {})
   * .catch(() => {})
   * @memberof Carsharing
   */
  associateVehicleDriver(queryParams) {
    return this.carsharingController.associateVehicleDriver(queryParams);
  }

  /**
   *
   * @param {String} workFlowStep
   * Jumio Auth Link
   * @memberof Carsharing
   * Carsharing.getJumioToken("authentcation")
   * .then(() => {})
   * .catch(() => {})
   */
  getJumioAuthLink(queryParams) {
    return this.carsharingController.getJumioAuthLink(queryParams);
  }

  /**
   * Jumio Auth Status
   * @param {Number} customerId
   * @param {Srting} workflowId
   *
   * Carsharing.getAuthStatus("authentcation")
   * .then(() => {})
   * .catch(() => {})
   */

  getJumioAuthStatus(queryParams) {
    return this.carsharingController.getJumioAuthStatus(queryParams);
  }

  /**
   * Get Inspection report
   *
   * @param {Number} rentalId
   * @param {Number} vehicleId
   * @param {Boolean} base64
   * default value true
   *
   * @memberof Carsharing
   *
   * Carsharing.getInspectionReport(1, 2)
   * .then(() => {})
   * .catch(() => {})
   */

  getInspectionReport(queryParams) {
    return this.carsharingController.getInspectionReport(queryParams);
  }

  /**
   * Get Inspection report
   *
   * @param {Number} rentalId
   * @param {Number} vehicleId
   * @param {Number} customerId
   * @param {Boolean} base64
   * default value true
   *
   * @memberof Carsharing
   *
   * Carsharing.getRentalAgreement(1, 2, 3)
   * .then(() => {})
   * .catch(() => {})
   */

  getRentalAgreement(queryParams) {
    return this.carsharingController.getRentalAgreement(queryParams);
  }

  /**
   * Get Inspection report
   *
   * @param {Number} rentalId
   * @param {Number} vehicleId
   * @param {Object} data
   *
   * @memberof Carsharing
   *
   * Carsharing.signInspectionReport(1, {...}})
   * .then(() => {})
   * .catch(() => {})
   */
  signInspectionReport(queryParams) {
    return this.carsharingController.signInspectionReport(queryParams);
  }

  /**
   * Get Inspection report
   *
   * @param {Number} rentalId
   * @param {Number} vehicleId
   * @param {Object} data
   *
   * @memberof Carsharing
   *
   * Carsharing.signRentalAgreement(1, {...}})
   * .then(() => {})
   * .catch(() => {})
   */
  signRentalAgreement(queryParams) {
    return this.carsharingController.signRentalAgreement(queryParams);
  }

  /**
   * get Rental Info related to customer
   *
   * @memberof Carsharing
   *
   * Carsharing.getCustomerRentalInfo()
   * .then(() => {})
   * .catch(() => {})
   */

  getCustomerRentalInfo() {
    return this.carsharingController.getCustomerRentalInfo();
  }

  /**
   * update vehicle door status
   * @param {Number} vehicleId
   * @param {String} doorStatus
   *
   * Carsharing.updateDoorStatus(1, "unlock")
   * .then(() => {})
   * .catch(() => {})
   *
   * @memberof Carsharing
   */
  updateDoorStatus(queryParams) {
    return this.carsharingController.updateDoorStatus(queryParams);
  }

  /**
   * create new carsharing rental
   * @param {Number} vehicleId
   * @param {Number} customerId
   *
   * Carsharing.createCarSharingRenal(1, 2)
   * .then(() => {})
   * .catch(() => {})
   *
   * @memberof Carsharing
   */

  createCarSharingRental(queryParams) {
    return this.carsharingController.createCarSharingRental(queryParams);
  }

  /**
   * get carsharing rental info
   * @param {Number} id
   *
   * Carsharing.getCarSharingRenal(id)
   * .then(() => {})
   * .catch(() => {})
   * @memberof Carsharing
   */

  getCarsharingRental(queryParams) {
    return this.carsharingController.getCarsharingRental(queryParams);
  }

  /**
   * end carsharing rental
   * @param {Number} id
   *
   * Carsharing.endCarSharingRental(1)
   * .then(() => {})
   * .catch(() => {})
   */

  endCarSharingRental(queryParams) {
    return this.carsharingController.endCarSharingRental(queryParams);
  }

  /**
   *
   * @param {Number} fleetId
   * @param {Number} vehicleId
   *
   * Carsharing.removeVehicleFromFleet(1, 2)
   * .then(() => {})
   * .catch(() => {})
   *
   * @memberof Carsharing
   */

  removeVehicleFromFleet(queryParams) {
    return this.carsharingController.removeVehicleFromFleet(queryParams);
  }

  /**
   *
   * @param {Number} id
   * @param {Number} rating
   * @param {String} comment
   *
   * Carsharing.postRating(id, rating, comment)
   * .then(() => {})
   * .catch(() => {})
   *
   * @memberof Carsharing
   */

  postRating(queryParams) {
    return this.carsharingController.postRating(queryParams);
  }

  /**
   * Gets vehicle type leasing entities.
   *
   * @param {number} vehicle_type vehicle type ID.
   *
   * @returns {Object} response Response with the VehicleTypeLeasingEntities.
   */

  getVehicleTypeLeasingEntities(queryParams) {
    return this.carsharingController.getVehicleTypeLeasingEntities(queryParams);
  }

  /**
   * Gets vehicle type leasing entity details.
   *
   * @param {number} vehicle_type vehicle type ID.
   *
   * @returns {Object} response Response with the VehicleTypeLeasingEntityDetails.
   */

  getVTLEDetails(queryParams) {
    return this.carsharingController.getVTLEDetails(queryParams);
  }

  /**
		 * Gets station vehicle types
		 *
		 * @param {number} station_id station ID.
		 * @param {string} start_date_time Start Date time
		 * @param {string} end_date_time End Date time

		 *
		 * @returns {Object} response Response with the StationVehicleTypes.
		 */
  getStationVehicleTypes(queryParams) {
    return this.carsharingController.getStationVehicleTypes(queryParams);
  }

  /**
			* Gets specific vehicle type
			*
			* @param {number} station_id station ID.
			* @param {string} start_date_time Start Date time
			* @param {string} end_date_time End Date time

			*
			* @returns {Object} response Response with the StationVehicleTypes.
			*/
  getVehicleType(id) {
    return this.carsharingController.getVehicleType(id);
  }

  /**
   * Creates a scheduled rental contract (aka subscription).
   *
   * @param {Object} queryParams need to include:
   * 
   * "start_location":{
      "address":"",
      "postal_code":"",
      "city":"",
      "country":""
   },
   * "start_station":"",
   * "service_id":<#>,
   * "vehicle_type_id": <#>,
   * "start_date_time":"",
   * "end_date_time":"",
   * "vehicle_assignment_method":"",
   * "vehicle_type_leasing_entity_id": <#>,
   * "delivery_contact_details":{
      "email":"",
      "phone_number":"",
      "first_name":"",
      "last_name":""
   }
   */
  createContract(params) {
    return this.carsharingController.createContract(params);
  }

  /**
   * Get active subscription
   */
  getSubscription(params) {
    return this.carsharingController.getSubscription(params);
  }

  /**
   * Get subscription provider configuration
   */
  getSubscriptionProviderConfig(params) {
    return this.carsharingController.getSubscriptionProviderConfig(params);
  }

  /**
   * Get addons
   */
  getAddons(params) {
    return this.carsharingController.getAddons(params);
  }

  /**
   * Get addon
   *
   * @param {number} addonId addon ID.
   *
   * @returns {Object} response Response with the addon details.
   */
  getAddon(params) {
    return this.carsharingController.getAddon(params);
  }

  /**
   * Get addon types
   */
  getAddonTypes(params) {
    return this.carsharingController.getAddonTypes(params);
  }

  /**
   * Get addon type
   *
   * @param {number} addonTypeId addon type ID.
   *
   * @returns {Object} response Response with the addon type details.
   */
  getAddonType(params) {
    return this.carsharingController.getAddonType(params);
  }

  /**
   * Get leasing addons rate
   *
   * @param {array} addons addon IDs
   * @param {number} vehicle_type_leasing_entity_id vehicle type leasing entity ID
   *
   * @returns {Object} response Response with the leasing addons rate.
   */
  getLeasingAddonsRate(params) {
    return this.carsharingController.getLeasingAddonsRate(params);
  }
}

/**
 * Carsharing singleton object for interacting with Carsharing components.
 * @namespace Carsharing
 */
export let Carsharing = new _Carsharing();
